import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, Phone, TrendingUp } from "lucide-react";
import { api, adminApi } from "@/lib/sleek";
import PortfolioManager from "@/components/admin/PortfolioManager";
import ShowroomManager from "@/components/admin/ShowroomManager";
import LandingManager from "@/components/admin/LandingManager";
import CallsManager from "@/components/admin/CallsManager";
import SyncToProd from "@/components/admin/SyncToProd";
import LeadReply from "@/components/admin/LeadReply";

const KEY = "sleek_admin_token";

// Stacked-bar palette — deliberately muted, not rainbow. Order matches sorted breakdown.
const UTM_BAR_COLORS = [
  "bg-zinc-900",     // #1 channel — dominant
  "bg-indigo-600",   // #2 — accent
  "bg-amber-500",    // #3 — warm contrast
  "bg-emerald-600",  // #4 — green
  "bg-rose-500",     // #5 — pink
  "bg-zinc-400",     // 6+ — neutral catch-all
];

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY) || "");
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [utm, setUtm] = useState(null);
  const [abStats, setAbStats] = useState(null);
  const [missedCalls, setMissedCalls] = useState([]);
  const [tab, setTab] = useState("leads");
  const [loading, setLoading] = useState(false);
  const [replyLead, setReplyLead] = useState(null);

  const logout = useCallback(() => {
    const t = sessionStorage.getItem(KEY);
    if (t) adminApi(t).post("/admin/logout").catch(() => {});
    sessionStorage.removeItem(KEY);
    setToken("");
  }, []);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const c = adminApi(token);
      const [s, l, b, an, u, ab, calls] = await Promise.all([
        c.get("/admin/stats"),
        c.get("/admin/leads"),
        c.get("/admin/bookings"),
        c.get("/admin/portfolio-analytics"),
        c.get("/admin/utm-breakdown"),
        c.get("/admin/ab-stats"),
        c.get("/admin/calls"),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setBookings(b.data);
      setAnalytics(an.data);
      setUtm(u.data);
      setAbStats(ab.data);
      setMissedCalls(calls.data.filter((x) => !x.answered && !x.resolved));
      setAnalyticsLoading(false);
    } catch (err) {
      setAnalyticsLoading(false);
      if (err?.response?.status === 401) logout();
    }
  }, [token, logout]);

  useEffect(() => { if (token) refresh(); }, [token, refresh]);
  useEffect(() => { if (token && (tab === "leads" || tab === "calls")) refresh(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", creds);
      sessionStorage.setItem(KEY, data.token);
      setToken(data.token);
    } catch {
      toast.error("Invalid credentials.");
    } finally { setLoading(false); }
  };

  const updateLead = async (id, status) => {
    await adminApi(token).patch(`/admin/leads/${id}`, { status });
    refresh();
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await adminApi(token).delete(`/admin/leads/${id}`);
    refresh();
  };

  const resolveCall = async (id) => {
    await adminApi(token).patch(`/admin/calls/${id}/resolve`);
    refresh();
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-md border border-zinc-800 p-10" data-testid="admin-login-form">
          <div className="font-display text-3xl">Admin Dashboard</div>
          <div className="text-zinc-400 text-sm mt-2">Sleek Renovations · internal access</div>
          <input
            placeholder="Username"
            value={creds.username}
            onChange={(e) => setCreds({ ...creds, username: e.target.value })}
            className="w-full bg-transparent border-b border-zinc-700 py-3 mt-10 focus:outline-none focus:border-white"
            data-testid="admin-username"
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={creds.password}
            onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            className="w-full bg-transparent border-b border-zinc-700 py-3 mt-6 focus:outline-none focus:border-white"
            data-testid="admin-password"
            required
          />
          <button disabled={loading} className="w-full bg-white text-zinc-950 py-3 mt-10 hover:bg-zinc-200 transition disabled:opacity-50" data-testid="admin-login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="font-display text-xl">Sleek · Admin Console</div>
          <div className="flex items-center gap-5">
            <SyncToProd token={token} />
            <button onClick={logout} className="text-sm flex items-center gap-2 text-zinc-600 hover:text-zinc-950" data-testid="admin-logout"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-10">
        {/* stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "Total Leads", v: stats?.total_leads ?? "—" },
            { k: "New Leads", v: stats?.new_leads ?? "—" },
            { k: "Total Bookings", v: stats?.total_bookings ?? "—" },
            { k: "Pending Bookings", v: stats?.pending_bookings ?? "—" },
          ].map((s) => (
            <div key={s.k} className="bg-white border border-zinc-200 p-6">
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{s.k}</div>
              <div className="font-display text-4xl font-light mt-3">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Hero headline A/B test */}
        <div className="mt-10 bg-white border border-zinc-200 p-6 md:p-8" data-testid="admin-ab-test">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Landing page · hero headline A/B test</div>
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            {[
              { v: "A", label: "\u201CYour Next Kitchen Starts with the Right Cabinetry.\u201D" },
              { v: "B", label: "\u201CThe Kitchen You've Always Wanted. It Starts with the Cabinetry.\u201D" },
            ].map(({ v, label }) => {
              const d = abStats?.[v] || { views: 0, leads: 0, conversion: 0 };
              return (
                <div key={v} className="border border-zinc-200 p-5" data-testid={`ab-variant-${v.toLowerCase()}`}>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-zinc-950 text-white text-xs font-semibold">{v}</span>
                    <span className="text-sm text-zinc-700">{label}</span>
                  </div>
                  <div className="mt-4 flex gap-8 text-sm">
                    <div><span className="font-display text-2xl font-light">{d.views}</span> <span className="text-zinc-500">visitors</span></div>
                    <div><span className="font-display text-2xl font-light">{d.leads}</span> <span className="text-zinc-500">leads</span></div>
                    <div><span className="font-display text-2xl font-light">{d.conversion}%</span> <span className="text-zinc-500">conversion</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Portfolio engagement — traffic + conversion per piece */}
        <div className="mt-10 bg-white border border-zinc-200 p-6 md:p-8" data-testid="admin-portfolio-engagement">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <TrendingUp size={18} className="text-zinc-900" />
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Portfolio engagement · traffic vs. conversion</div>
            </div>
            <div className="text-xs text-zinc-500">Sorted by views · top 8</div>
          </div>

          {analyticsLoading ? (
            <div className="mt-6 space-y-3" data-testid="admin-engagement-skeleton">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 animate-pulse">
                  <div className="w-12 h-12 bg-zinc-100" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-2/5 bg-zinc-200" />
                    <div className="h-2 w-1/5 bg-zinc-100" />
                  </div>
                  <div className="h-5 w-8 bg-zinc-100" />
                  <div className="h-5 w-8 bg-zinc-100" />
                  <div className="h-5 w-12 bg-zinc-100" />
                  <div className="h-5 w-32 bg-zinc-100" />
                </div>
              ))}
            </div>
          ) : analytics.length === 0 ? (
            <div className="text-zinc-500 text-sm py-6">
              No portfolio activity yet. Views start counting the moment a visitor opens any <code className="bg-zinc-100 px-1.5 py-0.5">/portfolio/:id</code> page; leads count when they use the &ldquo;Get this look&rdquo; CTA.
            </div>
          ) : (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-engagement-table">
                <thead className="text-left text-[10px] tracking-[0.25em] uppercase text-zinc-500 border-b border-zinc-200">
                  <tr>
                    <th className="py-3 pr-4">#</th>
                    <th className="py-3 pr-4">Project</th>
                    <th className="py-3 pr-4 text-right">Views</th>
                    <th className="py-3 pr-4 text-right">Leads</th>
                    <th className="py-3 pr-4 text-right">Conv.</th>
                    <th className="py-3 pl-4">Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.map((p, idx) => {
                    // Diagnostic signal: high views, low conversion
                    let signal = null;
                    if (p.views >= 10 && p.leads === 0) {
                      signal = { tone: "warn", label: "Eyes, no leads — rewrite the copy" };
                    } else if (p.views >= 5 && p.conversion_rate >= 10) {
                      signal = { tone: "good", label: "Top converter — shoot more like this" };
                    } else if (p.views === 0 && p.leads > 0) {
                      signal = { tone: "info", label: "Leads without views — referral?" };
                    }
                    return (
                      <tr
                        key={p.id}
                        className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition"
                        data-testid={`admin-engagement-row-${p.id}`}
                      >
                        <td className="py-3 pr-4 text-zinc-400 font-mono text-xs">{idx + 1}</td>
                        <td className="py-3 pr-4">
                          <Link
                            to={`/portfolio/${p.id}`}
                            target="_blank"
                            rel="noopener"
                            className="flex items-center gap-3 group"
                          >
                            <img src={p.image} alt="" className="w-12 h-12 object-cover bg-zinc-100" />
                            <div>
                              <div className="font-medium leading-tight group-hover:underline">{p.title}</div>
                              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-0.5">{p.category}</div>
                            </div>
                          </Link>
                        </td>
                        <td className="py-3 pr-4 text-right font-display text-lg">{p.views}</td>
                        <td className="py-3 pr-4 text-right font-display text-lg">{p.leads}</td>
                        <td className="py-3 pr-4 text-right font-mono text-zinc-700">
                          {p.views > 0 ? `${p.conversion_rate}%` : "—"}
                        </td>
                        <td className="py-3 pl-4">
                          {signal ? (
                            <span
                              className={`inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-1 ${
                                signal.tone === "warn"
                                  ? "bg-amber-50 text-amber-900 border border-amber-200"
                                  : signal.tone === "good"
                                  ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
                                  : "bg-zinc-100 text-zinc-700 border border-zinc-200"
                              }`}
                            >
                              {signal.label}
                            </span>
                          ) : (
                            <span className="text-zinc-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* UTM source breakdown — which channels drive leads */}
        {utm && utm.total_all_time > 0 && (
          <div className="mt-10 bg-white border border-zinc-200 p-6 md:p-8" data-testid="admin-utm-breakdown">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Lead sources · which channels are paying off</div>
              <div className="text-xs text-zinc-500">
                <span className="text-zinc-950 font-medium">{utm.total_this_week}</span> leads this week ·{" "}
                <span className="text-zinc-700">{utm.total_all_time}</span> all-time ·{" "}
                <span className="text-emerald-700 font-medium">{utm.total_bookings || 0}</span> booked
              </div>
            </div>

            {/* Slim stacked bar */}
            <div className="mt-5 h-2 w-full bg-zinc-100 flex overflow-hidden">
              {utm.breakdown.map((b, idx) => {
                const pct = (b.all_time / utm.total_all_time) * 100;
                return (
                  <div
                    key={b.source}
                    className={`h-full ${UTM_BAR_COLORS[idx % UTM_BAR_COLORS.length]}`}
                    style={{ width: `${pct}%` }}
                    title={`${b.source}: ${b.all_time} leads → ${b.bookings} booked (${b.conversion_rate}%)`}
                  />
                );
              })}
            </div>

            {/* Per-channel conversion grid */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
              {utm.breakdown.map((b, idx) => (
                <div
                  key={b.source}
                  className="flex items-start gap-3"
                  data-testid={`admin-utm-${b.source}`}
                  title={`${b.bookings} of ${b.all_time} leads converted to a booking`}
                >
                  <span className={`inline-block w-2.5 h-2.5 mt-1.5 ${UTM_BAR_COLORS[idx % UTM_BAR_COLORS.length]}`} />
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="capitalize text-zinc-900 font-medium text-sm">{b.source}</span>
                      {b.this_week > 0 && (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-emerald-700 bg-emerald-50 px-1.5 py-0.5 border border-emerald-200">
                          +{b.this_week} this wk
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-zinc-600 leading-tight">
                      <span className="font-display text-lg text-zinc-900 font-light">{b.all_time}</span>
                      <span className="text-zinc-400 mx-1.5">→</span>
                      <span className="font-display text-lg text-zinc-900 font-light">{b.bookings}</span>
                      <span className="text-xs text-zinc-500 ml-1">booked</span>
                    </div>
                    <div
                      className={`text-[10px] tracking-[0.2em] uppercase mt-1 ${
                        b.conversion_rate >= 25 ? "text-emerald-700" : b.conversion_rate >= 10 ? "text-zinc-700" : "text-zinc-400"
                      }`}
                    >
                      {b.conversion_rate}% conversion
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* tabs */}
        <div className="flex gap-2 mt-10">
          {["leads", "bookings", "calls", "portfolio", "showroom", "landing"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-xs tracking-[0.25em] uppercase px-4 py-2 border ${tab === t ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-700"}`}
              data-testid={`admin-tab-${t}`}
            >{t}</button>
          ))}
        </div>

        {tab === "portfolio" && <PortfolioManager token={token} />}
        {tab === "showroom" && <ShowroomManager token={token} />}
        {tab === "landing" && <LandingManager token={token} />}
        {tab === "calls" && <CallsManager token={token} onChange={refresh} />}

        {tab === "leads" && (
          <div className="mt-6 bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-zinc-50 text-left text-[11px] tracking-[0.2em] uppercase text-zinc-500">
                <tr><th className="p-4">Date</th><th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Project</th><th className="p-4">Source / Ad</th><th className="p-4">Status</th><th className="p-4"></th></tr>
              </thead>
              <tbody>
                {missedCalls.map((mc) => (
                  <tr key={`call-${mc.id}`} className="border-t border-zinc-100 align-top bg-amber-50/40" data-testid={`admin-missed-call-${mc.id}`}>
                    <td className="p-4 text-zinc-500 whitespace-nowrap">{new Date(mc.created_at).toLocaleString()}</td>
                    <td className="p-4 font-medium">
                      Missed Call
                      <div className="text-xs text-zinc-500 mt-1">
                        {mc.sms_sent ? "Text-back sent" : mc.sms_sent === false ? "Text-back failed" : "No text sent yet"}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 text-zinc-700"><Phone size={12} /> <a href={`tel:${mc.from_number}`} className="hover:underline">{mc.from_number || "Unknown"}</a></div>
                    </td>
                    <td className="p-4 text-zinc-400">—</td>
                    <td className="p-4">
                      <span className="inline-block text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300">missed call</span>
                    </td>
                    <td className="p-4">
                      <span className="inline-block text-[10px] tracking-[0.2em] uppercase px-2 py-1 bg-amber-100 text-amber-900 border border-amber-300">pending text-back</span>
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button onClick={() => resolveCall(mc.id)} className="text-xs text-zinc-600 hover:text-zinc-950 underline" data-testid={`admin-call-resolve-${mc.id}`}>Mark resolved</button>
                    </td>
                  </tr>
                ))}
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-zinc-100 align-top" data-testid={`admin-lead-${l.id}`}>
                    <td className="p-4 text-zinc-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="p-4 font-medium">{l.name}<div className="text-xs text-zinc-500 max-w-[24ch] mt-1">{l.message}</div></td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 text-zinc-700"><Mail size={12} /> <a href={`mailto:${l.email}`} className="hover:underline">{l.email}</a></div>
                      {l.phone && <div className="flex items-center gap-1 text-zinc-700 mt-1"><Phone size={12} /> {l.phone}</div>}
                    </td>
                    <td className="p-4">
                      {l.project_type || "—"}
                      <div className="text-xs text-zinc-500">{l.budget}</div>
                    </td>
                    <td className="p-4 capitalize">
                      {l.source}
                      {l.utm_source ? (
                        <div className="mt-1 normal-case" data-testid={`admin-lead-utm-${l.id}`}>
                          <span className="inline-block text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 bg-indigo-50 text-indigo-900 border border-indigo-200">
                            ad · {l.utm_source}{l.utm_medium ? `/${l.utm_medium}` : ""}
                          </span>
                          {l.utm_campaign && <div className="text-[11px] text-zinc-500 mt-0.5 max-w-[20ch]">{l.utm_campaign}</div>}
                        </div>
                      ) : (
                        <div className="text-[11px] text-zinc-400 mt-1 normal-case">organic / direct</div>
                      )}
                    </td>
                    <td className="p-4">
                      <select value={l.status} onChange={(e) => updateLead(l.id, e.target.value)} className="border border-zinc-300 px-2 py-1" data-testid={`admin-lead-status-${l.id}`}>
                        {!["new", "contacted", "quoted", "won", "lost"].includes(l.status) && <option value={l.status}>{l.status}</option>}
                        <option value="new">new</option>
                        <option value="contacted">contacted</option>
                        <option value="quoted">quoted</option>
                        <option value="won">won</option>
                        <option value="lost">lost</option>
                      </select>
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button onClick={() => setReplyLead(l)} className="text-zinc-500 hover:text-zinc-950 mr-3" title="Reply with template" data-testid={`admin-lead-reply-${l.id}`}><Mail size={14} /></button>
                      <button onClick={() => deleteLead(l.id)} className="text-zinc-500 hover:text-red-600" data-testid={`admin-lead-delete-${l.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && missedCalls.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-zinc-500">No leads yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "bookings" && (
          <div className="mt-6 bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-bookings-table">
              <thead className="bg-zinc-50 text-left text-[11px] tracking-[0.2em] uppercase text-zinc-500">
                <tr><th className="p-4">Date · Time</th><th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Project</th><th className="p-4">Address</th><th className="p-4">Status</th><th className="p-4"></th></tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-zinc-100 align-top" data-testid={`admin-booking-${b.id}`}>
                    <td className="p-4 whitespace-nowrap">{b.preferred_date}<div className="text-xs text-zinc-500">{b.preferred_time}</div>
                      {b.reminder_sent && (
                        <div className="inline-block mt-1 text-[10px] tracking-[0.15em] uppercase px-1.5 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid={`admin-booking-reminded-${b.id}`}>Reminded ✓</div>
                      )}
                    </td>
                    <td className="p-4 font-medium">{b.name}<div className="text-xs text-zinc-500 max-w-[24ch] mt-1">{b.notes}</div></td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 text-zinc-700"><Mail size={12} /> {b.email}</div>
                      <div className="flex items-center gap-1 text-zinc-700 mt-1"><Phone size={12} /> {b.phone}</div>
                    </td>
                    <td className="p-4">{b.project_type}</td>
                    <td className="p-4 text-zinc-700">{b.address || "—"}</td>
                    <td className="p-4">
                      <select
                        value={b.status}
                        onChange={async (e) => { await adminApi(token).patch(`/admin/bookings/${b.id}`, { status: e.target.value }); refresh(); }}
                        className="border border-zinc-300 px-2 py-1"
                        data-testid={`admin-booking-status-${b.id}`}
                      >
                        <option value="pending">pending</option>
                        <option value="confirmed">confirmed</option>
                        <option value="completed">completed</option>
                        <option value="cancelled">cancelled</option>
                      </select>
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => setReplyLead({ ...b, __booking: true })} className="text-zinc-500 hover:text-zinc-950" title="Reply with template" data-testid={`admin-booking-reply-${b.id}`}><Mail size={14} /></button>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-zinc-500">No bookings yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {replyLead && <LeadReply lead={replyLead} isBooking={!!replyLead.__booking} onClose={() => setReplyLead(null)} />}
    </div>
  );
}
